/* eslint-disable no-undef */
import { tabletMax } from '@propertypal/shared/src/constants/mediaQueries';
import { Property, Agent } from '@propertypal/shared/src/types/property';
import { EnquiryContactDetails } from '@propertypal/shared/src/utils/enquiries/getContact';
import { getAgentColours } from '@propertypal/shared/src/utils/property/getSellingAgent';
import Link from 'next/link';
import React, { CSSProperties, FunctionComponent, useEffect, useRef, useState } from 'react';
import Button from '../buttons/Button';
import { RowAlignCenter } from '../layout';
import useMediaQuery from '../media-query/useMediaQuery';
import {
  AgentText,
  AgentBoxFixed,
  AgentBoxFixedButtons,
  LicenceWarning,
  NameAndPSRTextWrapper,
} from './PropertyAgentBoxFixed.style';
import PropertyPhoneNumberButton from './PropertyPhoneNumberButton';

interface Props {
  containerStyle?: CSSProperties;
  property: Property;
  onCallClick: (location: string) => void;
  onShowNumberClick: (location: string) => void;
  enquireNowClick: (location: string) => void;
  registerInterestClick: (location: string) => void;
  requestValuationClick: () => void;
  registerInterest?: boolean;
  requestValuation?: boolean;
  leadId: number;
  sellingAgent: Agent | undefined;
  contactInfo: EnquiryContactDetails;
  leadsLink: string;
}

const getTotalButtons = (
  props: Pick<Props, 'registerInterest' | 'requestValuation'>,
  contactInfo: EnquiryContactDetails,
) => {
  let total = 0;

  if (contactInfo.email) total += 1;
  if (contactInfo.phone) total += 1;
  if (props.registerInterest) total += 1;
  if (props.requestValuation) total += 1;

  return total;
};

const PropertyAgentBoxFixed: FunctionComponent<Props> = ({
  property,
  sellingAgent,
  contactInfo,
  leadsLink,
  requestValuationClick,
  registerInterestClick,
  enquireNowClick,
  ...props
}) => {
  const { pdpBrandingButtonBgColour, pdpBrandingButtonTextColour } = getAgentColours(
    property.enhancedBranding,
    sellingAgent,
  );
  const contactBox = useRef<HTMLDivElement | null>(null);
  const [showEnquiryBar, setShowEnquiryBar] = useState(false);
  const totalButtons = getTotalButtons(props, contactInfo);
  const isMobile = useMediaQuery({ query: `(${tabletMax})` });
  const useShortText = isMobile && totalButtons >= 4;

  useEffect(() => {
    const checkBelowThreshold: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        setShowEnquiryBar(entry.boundingClientRect.top < 0);
      });
    };

    const observer = new IntersectionObserver(checkBelowThreshold, {
      threshold: 1,
    });

    if (contactBox.current) {
      observer.observe(contactBox.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <AgentBoxFixed
        className="pp-contact-agent hide-print"
        data-testid="agent-box-fixed"
        style={{ top: showEnquiryBar ? 0 : -60, visibility: showEnquiryBar ? 'visible' : 'hidden' }}
      >
        {sellingAgent && (
          <RowAlignCenter>
            {sellingAgent.logo && sellingAgent.logo.standard && (
              <img
                src={sellingAgent.logo.standard}
                alt={`${property.development ? 'Developer' : 'Agent'} Logo`}
                style={{ marginRight: 15 }}
                data-testid="agent-logo-fixed"
              />
            )}
            <NameAndPSRTextWrapper>
              <AgentText>{sellingAgent.organisation}</AgentText>
              {sellingAgent.psrLicenceNumber && property.countryCode === 'IRL' && (
                <AgentText>
                  PSR Licence: {sellingAgent.psrLicenceNumber}
                  {sellingAgent.psrLicenceNumber === 'NO LICENCE' && (
                    <LicenceWarning href="/psr-licence-number" prefetch={false} style={{ textAlign: 'center' }}>
                      ?
                    </LicenceWarning>
                  )}
                </AgentText>
              )}
            </NameAndPSRTextWrapper>
          </RowAlignCenter>
        )}

        {!sellingAgent && (
          <RowAlignCenter>
            <AgentText>Marketed by multiple agents</AgentText>
          </RowAlignCenter>
        )}

        {property.published && (
          <AgentBoxFixedButtons numBtns={totalButtons}>
            {props.requestValuation && (
              <Button
                containerStyle={{ height: 50 }}
                onClick={requestValuationClick}
                testID="requestValuationButton"
                type="button"
                color={pdpBrandingButtonBgColour}
                fontColor={pdpBrandingButtonTextColour}
              >
                {useShortText ? 'Valuation' : 'Request a Valuation'}
              </Button>
            )}

            {props.registerInterest && !leadsLink && (
              <Button
                containerStyle={{ height: 50 }}
                onClick={() => registerInterestClick('Sticky Bar')}
                testID="registerInterestFixed"
                type="button"
                className="pp-register-interest"
              >
                {useShortText ? 'Register' : 'Register Your Interest'}
              </Button>
            )}

            {leadsLink && (
              <Link href={leadsLink} style={{ width: '100%' }}>
                <Button containerStyle={{ height: 50 }} type="button">
                  {useShortText ? 'Message' : 'Send another message'}
                </Button>
              </Link>
            )}

            {contactInfo.email && !leadsLink && (
              <Button
                containerStyle={{ height: 50 }}
                onClick={() => enquireNowClick('Sticky Bar')}
                testID="enquireNowFixed"
                type="button"
                color={pdpBrandingButtonBgColour}
                fontColor={pdpBrandingButtonTextColour}
              >
                {useShortText ? 'Email' : props.registerInterest ? 'Email the Agent' : 'Enquire Now'}
              </Button>
            )}

            {contactInfo.phone && (
              <PropertyPhoneNumberButton
                {...props}
                containerStyle={{ height: 50 }}
                location="Sticky Bar"
                color={pdpBrandingButtonBgColour}
                fontColor={pdpBrandingButtonTextColour}
                useShortText={useShortText}
              />
            )}
          </AgentBoxFixedButtons>
        )}
      </AgentBoxFixed>

      <div ref={contactBox} />
    </>
  );
};

export default PropertyAgentBoxFixed;
